import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils";

/**
 * StatusDot — basis extension (no canonical shadcn component). A small colored dot for presence /
 * liveness, optionally pulsing and optionally labelled. Used for "Live now" on an active
 * conversation, agent online state, etc.
 *
 *   <StatusDot variant="live" pulse />                 // bare pulsing dot
 *   <StatusDot variant="live" pulse>Live now</StatusDot> // labelled (text matches the variant color)
 */
const statusDotVariants = cva("inline-block shrink-0 rounded-full", {
  variants: {
    variant: {
      online: "bg-success",
      live: "bg-success",
      busy: "bg-warning",
      offline: "bg-muted-foreground",
      error: "bg-destructive",
    },
    size: { sm: "size-1.5", default: "size-2", lg: "size-2.5" },
  },
  defaultVariants: { variant: "online", size: "default" },
});

const DOT_BG = {
  online: "bg-success",
  live: "bg-success",
  busy: "bg-warning",
  offline: "bg-muted-foreground",
  error: "bg-destructive",
} as const;

const DOT_SIZE = { sm: "size-1.5", default: "size-2", lg: "size-2.5" } as const;

const LABEL_COLOR = {
  online: "text-success",
  live: "text-success",
  busy: "text-warning",
  offline: "text-muted-foreground",
  error: "text-destructive",
} as const;

function StatusDot({
  className,
  variant,
  size,
  pulse = false,
  children,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof statusDotVariants> & { pulse?: boolean }) {
  const v = variant ?? "online";
  const s = size ?? "default";

  const mark = pulse ? (
    <span className={cn("relative inline-flex", DOT_SIZE[s])} aria-hidden="true">
      <span className={cn("absolute inline-flex size-full animate-ping rounded-full opacity-75", DOT_BG[v])} />
      <span className={cn("relative", statusDotVariants({ variant, size }))} />
    </span>
  ) : (
    <span className={statusDotVariants({ variant, size })} aria-hidden="true" />
  );

  if (children == null) {
    return (
      <span data-slot="status-dot" role="status" className={cn("inline-flex", className)} {...props}>
        {mark}
      </span>
    );
  }

  return (
    <span
      data-slot="status-dot"
      role="status"
      className={cn("inline-flex items-center gap-1.5 text-xs font-medium", LABEL_COLOR[v], className)}
      {...props}
    >
      {mark}
      <span>{children}</span>
    </span>
  );
}

export { StatusDot, statusDotVariants };
